import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import { AuthService, UserDirectoryItem } from '../../../services/AuthService';
import { getUserInfo } from '../../../utils/auth';
import { UserRole, ROLE_LABEL, normalizeRole } from '../../../utils/permissions';
import CreateUserDialog from '../dialogs/CreateUserDialog';
import EditUserDialog from '../dialogs/EditUserDialog';

const SELECTABLE_ROLES: UserRole[] = ['admin', 'editor', 'viewer'];

const ROLE_COLOR: Record<UserRole, 'error' | 'primary' | 'default'> = {
  admin: 'error',
  editor: 'primary',
  viewer: 'default',
};

/**
 * 유저 관리 탭 — admin 전용. 권한 부여(role 변경) / 삭제 / 생성.
 *
 * 본인 계정은 권한 변경/삭제 불가 (self-lockout 방지, 백엔드도 가드).
 * role 변경은 즉시 PATCH, 실패 시 직전 값으로 롤백.
 */
const UserManagementTab: React.FC = () => {
  const [users, setUsers] = useState<UserDirectoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [editTarget, setEditTarget] = useState<UserDirectoryItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserDirectoryItem | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'success' },
  );

  const myId = useMemo(() => getUserInfo()?.user_id, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await AuthService.listUsers();
      // 이름 가나다순 정렬 — 안정적 표시.
      setUsers([...list].sort((a, b) => a.name.localeCompare(b.name, 'ko')));
    } catch (e: any) {
      setError(e?.message || '유저 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  const handleRoleChange = async (user: UserDirectoryItem, nextRole: UserRole) => {
    const prevRole = user.role;
    if (prevRole === nextRole) return;
    setPendingId(user.id);
    // 낙관적 반영.
    setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, role: nextRole } : u)));
    try {
      await AuthService.changeUserRole(user.id, nextRole);
      notify(`${user.name} 님의 권한을 ${ROLE_LABEL[nextRole]}(으)로 변경했습니다.`, 'success');
    } catch (e: any) {
      // 롤백.
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, role: prevRole } : u)));
      notify(e?.message || '권한 변경 실패', 'error');
    } finally {
      setPendingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setPendingId(target.id);
    try {
      await AuthService.deleteUser(target.id);
      setUsers((prev) => prev.filter((u) => u.id !== target.id));
      notify(`${target.name} 님을 삭제했습니다.`, 'success');
    } catch (e: any) {
      notify(e?.message || '유저 삭제 실패', 'error');
    } finally {
      setPendingId(null);
      setDeleteTarget(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          유저 관리
        </Typography>
        <Chip label={`${users.length}명`} size="small" />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="새로고침">
          <span>
            <IconButton size="small" onClick={load} disabled={loading} aria-label="새로고침">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={() => setCreateOpen(true)}
          disableElevation
        >
          유저 생성
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>다시 시도</Button>}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
              <TableCell>이름</TableCell>
              <TableCell>아이디</TableCell>
              <TableCell>이메일</TableCell>
              <TableCell sx={{ width: 160 }}>권한</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  등록된 유저가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const isSelf = myId != null && Number(myId) === user.id;
                const role = normalizeRole(user.role);
                const busy = pendingId === user.id;
                return (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          {user.name}
                        </Typography>
                        {isSelf && <Chip label="나" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {isSelf ? (
                        <Chip label={ROLE_LABEL[role]} size="small" color={ROLE_COLOR[role]} />
                      ) : (
                        <Select
                          size="small"
                          value={role}
                          disabled={busy}
                          onChange={(e) => handleRoleChange(user, e.target.value as UserRole)}
                          sx={{ minWidth: 120, fontSize: '0.8rem' }}
                        >
                          {SELECTABLE_ROLES.map((r) => (
                            <MenuItem key={r} value={r} sx={{ fontSize: '0.8rem' }}>
                              {ROLE_LABEL[r]}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="아이디·이메일·비밀번호 변경">
                        <span>
                          <IconButton
                            size="small"
                            disabled={busy}
                            onClick={() => setEditTarget(user)}
                            aria-label={`${user.name} 정보 변경`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={isSelf ? '본인 계정은 삭제할 수 없습니다' : '삭제'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={isSelf || busy}
                            onClick={() => setDeleteTarget(user)}
                            aria-label={`${user.name} 삭제`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />

      <EditUserDialog
        open={!!editTarget}
        user={editTarget}
        onClose={() => setEditTarget(null)}
        onUpdated={() => {
          notify('유저 정보를 변경했습니다.', 'success');
          load();
        }}
      />

      <Dialog open={!!deleteTarget} onClose={() => pendingId == null && setDeleteTarget(null)}>
        <DialogTitle>유저 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}) 님을 삭제하시겠습니까?
            <br />
            삭제된 계정은 로그인할 수 없으며, 작성 기록은 보존됩니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={pendingId != null}>
            취소
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={pendingId != null}>
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagementTab;
